import {
  AdasBox,
  AdasTextField,
  AdasFormControl,
  AdasFormControlLabel,
  AdasSelect,
  AdasCheckbox,
  AdasPhoneField,
  AdasInputLabel,
  AdasAlert
} from '@components/wrapper-components'
import PropTypes from 'prop-types'
import { useState, useEffect, useCallback, useMemo } from 'react'

import { ChevronUp } from 'lucide-react'
import { UsaStates } from 'usa-states'
import { DATE_FORMATS, TEXT_CONSTANTS, MAX_NOTES_LENGTH } from 'constants'
import { getClients, getTimeSlots, getYears, getMakes, getModels, getReferralList } from 'api/api'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useOrganisationStore } from '@caradasstore/OrganisationStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useAvailabilityStore } from '@caradasstore/AvailabilityStore'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Logger } from 'logger'
import { VinInput } from '@components/vin'
import { AppointmentAttachment } from './AppointmentAttachment'
import { SchedulerDatePicker } from '../SchedulerDatePicker'
import { sortByName } from '../../../utils/utils'
import { formatDate } from '../../../utils/utils'
dayjs.extend(utc)

const Section = ({ label, children }) => (
  <div className='mb-6 rounded-lg border border-[#EBEDF0] overflow-hidden'>
    <div className='flex items-center justify-between bg-[#F1F4F8] px-4 py-2.5'>
      <span className='text-[14px] font-bold text-[#0A1628]'>{label}</span>
      <ChevronUp className='h-4 w-4 text-[#6B7280]' />
    </div>
    <div className='px-4 py-4 bg-white'>{children}</div>
  </div>
)

Section.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node
}

const SummaryRow = ({ label, value }) => (
  <div className='flex justify-between gap-4 py-1.5 text-sm'>
    <span className='text-[#6B7280]'>{label}</span>
    <span className='text-right font-medium text-[#0A1628] break-all'>{value || '—'}</span>
  </div>
)

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node
}

export const AppointmentForm = ({
  formValues,
  handleChange,
  setFormValues,
  formErrors,
  handleFileDelete,
  formValid,
  setAvailabilities,
  newTime,
  formattedDate,
  setFormValid,
  validationMap,
  formEditValues,
  quoteLocked = false,
  quoteCalibrationReport,
  quoteDocumentsLoading = false
}) => {
  const usStates = new UsaStates()
  const [timeSlots, setTimeSlots] = useState(null) // Initialize to null
  const setLoading = useLoadingStore((store) => store.setLoading)
  const organisationId = useOrganisationStore((store) => store.organisationId)
  const organisationDetail = useOrganisationStore((store) => store.organisationDetail)
  const availabilityDetail = useAvailabilityStore((state) => state.availabilityDetail)
  const isEditForm = formEditValues?.appointment_id > 0
  const ccId = useCenterStore((store) => store.ccId)
  const [years, setYears] = useState([])
  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [clients, setClients] = useState([])
  const [referralSourceList, setReferralSourceList] = useState([])

  const getMaxDateFromOrganisation = (minutes) => {
    const days = dayjs.duration(minutes, 'minutes').asDays()
    return dayjs()
      .add(Math.floor(days) - 1, 'days')
      .endOf('day')
  }

  // Usage
  const maxDateFromOrganisation = getMaxDateFromOrganisation(organisationDetail?.max_time)

  const formatTimeOrReturn = (time, toFormat) => {
    return time ? dayjs.utc(time, 'HH:mm').format(toFormat) : 'No time slots are available'
  }

  const convertMakesDataToIdValueArray = (data) => {
    return data.map((option) => {
      return { id: option.make, makeCode: option.makeCode, value: option.make }
    })
  }

  const convertModelDataToIdValueArray = (data) => {
    return data.map((option) => {
      return { id: option.description, value: option.description }
    })
  }

  const fetchVehicleData = async (year, makeCode) => {
    try {
      setLoading(true)

      // Fetch makes if a year is provided, and find the selected make if makeCode is available
      const makesResponse = year ? await getMakes({ year }) : { data: { makes: [] } }

      const makes = convertMakesDataToIdValueArray(makesResponse?.data?.makes || [])
      setMakes(makes)

      // Find the selected make using makeCode
      const selectedMake = makeCode ? makes.find((make) => make.value === makeCode) : null

      // Fetch models only if year and selectedMake are available
      const modelsResponse = year && selectedMake ? await getModels({ year, makeCode: selectedMake.makeCode }) : { data: { models: [] } }

      setModels(convertModelDataToIdValueArray(modelsResponse?.data?.models || []))

      // Fetch years data regardless of year/make selection
      const yearsResponse = await getYears()
      setYears(yearsResponse?.data?.years || [])
    } catch (error) {
      Logger.error({
        message: error,
        payload: { year, makeCode, path: 'fetchVehicleData' }
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!quoteLocked && !formValues.useVin) {
      fetchVehicleData(formValues.vehicleYear, formValues.vehicleMake ? formValues.vehicleMake : null)
    }
  }, [formValues.useVin, formValues.vehicleYear, formValues.vehicleMake])

  const handleVehicleFormChange = async (e) => {
    const { name, value } = e.target

    // Update form values based on the field being changed
    setFormValues((prevState) => ({
      ...prevState,
      vehicleMake: name === 'vehicleYear' ? '' : prevState.vehicleMake,
      vehicleModel: name === 'vehicleMake' ? '' : prevState.vehicleModel
    }))

    // Handle form changes based on which field is being updated
    if (name === 'vehicleYear') {
      await fetchVehicleData(value, null) // Fetch makes based on the selected year
    } else if (name === 'vehicleMake') {
      const selectedMake = makes.find((make) => make.value === value)
      if (selectedMake) {
        await fetchVehicleData(formValues?.vehicleYear, selectedMake.makeCode) // Fetch models based on year and selected make
      }
    }

    handleChange(e) // Call the generic handle change logic
  }

  useEffect(() => {
    if (formValues.appointmentDate) {
      const foundDate = Array.isArray(availabilityDetail)
        ? availabilityDetail.find(
            (date) =>
              formatDate(date.overrides_date, DATE_FORMATS.ISO_DATE_STRING) ===
              formatDate(formValues.appointmentDate, DATE_FORMATS.ISO_DATE_STRING)
          )
        : null // Handle the case when availabilityDetail is not an array

      // If you receive new data from `foundDate`, update the state
      if (foundDate && foundDate.slots) {
        const formattedAvailabilities = foundDate?.slots.map((slot) => ({
          ...slot,
          overrides_date: foundDate.overrides_date // Add overrides_date to each slot
        }))

        setAvailabilities({ overrides_date: foundDate.overrides_date, slots: formattedAvailabilities })
      } else {
        setAvailabilities({
          overrides_date: '', // Reset overrides_date to empty string
          slots: [] // Reset slots to an empty array
        })
      }

      fetchTimeSlots(formValues.appointmentDate)
    } else {
      setTimeSlots(null)
    }
  }, [formValues.appointmentDate])

  const updateVehicleInfo = useCallback((props) => {
    if (!props) {
      console.error('Vehicle info update failed: No vehicle data provided.')
      return
    }

    const { vin } = props

    // If VIN is empty, clear vehicle fields
    if (!vin) {
      setFormValues((prevState) => ({
        ...prevState,
        vehicleYear: '',
        vehicleMake: '',
        vehicleModel: '',
        vin: ''
      }))
      return
    }

    const { year, make, model } = props.data

    setFormValues((prevState) => ({
      ...prevState,
      vehicleYear: year || prevState.vehicleYear,
      vehicleMake: make || prevState.vehicleMake,
      vehicleModel: model || prevState.vehicleModel,
      vin: vin || prevState.vin
    }))
  }, [])

  useEffect(() => {
    if (formValues.vin && formValues.useVin) {
      // Simulate fetching vehicle details by VIN (replace with real API call)
      updateVehicleInfo()
    }
  }, [formValues.vin, formValues.useVin, updateVehicleInfo])

  const resetVehicleValues = useCallback(() => {
    setFormValues((prevState) => ({
      ...prevState,
      vehicleYear: '',
      vehicleMake: '',
      vehicleModel: '',
      vin: ''
    }))
    setMakes([])
    setModels([])
  }, [setFormValues])
  const vinChangeHandle = useCallback((e) => {
    resetVehicleValues()
    handleChange(e)
  }, [])

  const useExistingClientChangeHandle = useCallback((e) => {
    if (!formValues?.useExistingClient) {
      setFormValues((prevState) => ({
        ...prevState,
        businessName: '',
        primaryEmail: '',
        businessId: '',
        businessPhone: ''
      }))
    } else {
      setFormValues((prevState) => ({
        ...prevState,
        businessName: '',
        primaryEmail: '',
        businessId: '',
        businessPhone: ''
      }))
    }
    handleChange(e)
  }, [])

  const existingBusinessNameChangeHandle = (e) => {
    const { value } = e.target
    const client = clients?.find((client) => client.id === value)
    setFormValues((prevState) => ({
      ...prevState,
      businessId: client?.id || '',
      businessName: client?.name || '',
      primaryEmail: client?.email || '',
      businessPhone: client?.phone || ''
    }))
  }

  const fetchClient = async () => {
    const clientListResponse = await getClients({ ccid: ccId })
    if (clientListResponse.status === 200) {
      setClients(clientListResponse.data.sort(sortByName))
    }
  }

  useEffect(() => {
    if (!quoteLocked) fetchClient()
  }, [ccId])

  useEffect(() => {
    // An appointment already booked for a listed shop keeps that shop (CAR-4076); the name match only fills a missing one.
    if (!quoteLocked && clients.length > 0 && !formValues.businessId && formValues.businessName && formValues.primaryEmail) {
      const client = clients?.find(
        (client) =>
          client.name?.toLowerCase() === formValues.businessName?.toLowerCase() &&
          client.email?.toLowerCase() === formValues.primaryEmail?.toLowerCase() &&
          client.phone?.toLowerCase() === formValues.businessPhone?.toLowerCase()
      )
      setFormValues((prevState) => ({
        ...prevState,
        useExistingClient: client ? true : false,
        businessId: client?.id || ''
      }))
    }
  }, [clients, formValues.businessName, formValues.primaryEmail])

  const timeSlotChangeHandler = useCallback((e) => {
    if (e.target.value === 'default') {
      return
    }
    handleChange(e)
  }, [])

  const fetchTimeSlots = async (selectedDate) => {
    // No organisation yet (store still at its 0 default): the scheduler would answer 404.
    if (!organisationId) {
      setTimeSlots([])
      return
    }
    setLoading(true)
    const formattedDate = dayjs(selectedDate).format(DATE_FORMATS.ISO_DATE)
    const params = {
      ccId: ccId,
      organisationId: organisationId,
      date: formattedDate
    }
    try {
      const response = await getTimeSlots(params)
      setTimeSlots(response.data.length ? response.data : []) // Set empty array if no slots
    } catch (error) {
      Logger.error({
        message: error,
        payload: { params, path: 'fetchTimeSlots' }
      })
    } finally {
      setLoading(false)
    }
  }

  //   if (formValues.appointmentTime) {
  //     if (!timeSlots) {
  //       timeSlots = [formValues.appointmentTime]
  //     } else {
  //       const appointmentTimeExists = timeSlots.some((slot) => slot === formValues.appointmentTime)
  //       if (!appointmentTimeExists) {
  //         timeSlots.push(formValues.appointmentTime)
  //       }
  //     }
  //   }

  //   if (timeSlots?.length > 0) {
  //     return timeSlots.map((slot) => ({
  //       id: formatTimeOrReturn(slot, 'HH:mm'),
  //       value: formatTimeOrReturn(slot, DATE_FORMATS.ISO_TIME_STRING)
  //     }))
  //   }

  //   return []
  // }

  const generateAppointmentTimeSlotOptions = (timeSlots, formValues, newTime, isEditForm) => {
    if (!timeSlots || timeSlots.length === 0) {
      return {
        startOptions: [{ id: 'default', value: timeSlots === null ? 'Please select date first' : 'No time slots are available' }],
        endOptions: []
      }
    }

    const uniqueTimeSlots = new Set(timeSlots)

    if (newTime && formValues.appointmentDate && dayjs(formValues.appointmentDate).isSame(dayjs(formattedDate), 'day')) {
      uniqueTimeSlots.add(newTime) // Add newTime only if dates match
    }

    const startOptions = Array.from(uniqueTimeSlots).map((slot) => ({
      id: formatTimeOrReturn(slot, 'HH:mm'),
      value: formatTimeOrReturn(slot, DATE_FORMATS.ISO_TIME_STRING)
    }))

    // Conditionally generate endOptions only if isEditForm is true and timeSlots are available
    const endOptions = isEditForm
      ? Array.from(uniqueTimeSlots)
          .filter((slot) => slot > formValues.appointmentTime) // Filter slots greater than the selected start time
          .map((slot) => ({
            id: formatTimeOrReturn(slot, 'HH:mm'),
            value: formatTimeOrReturn(slot, DATE_FORMATS.ISO_TIME_STRING)
          }))
      : []

    return { startOptions, endOptions }
  }

  const { startOptions, endOptions } = useMemo(() => {
    return generateAppointmentTimeSlotOptions(timeSlots, formValues, newTime, isEditForm)
  }, [timeSlots, formValues, newTime, isEditForm])

  useEffect(() => {
    const fetchReferralSource = async () => {
      const referralListResponse = await getReferralList(ccId)
      if (referralListResponse?.status === 200) {
        const formattedReferralList = referralListResponse.data
          .sort((a, b) => a.company.localeCompare(b.company))
          .map((referralObj) => ({ id: referralObj.code, value: referralObj.company }))
        setReferralSourceList(formattedReferralList)
      }
    }

    if (!quoteLocked) fetchReferralSource()
  }, [])

  //   if (checked) {
  //     setFormValues((prevValues) => ({
  //       ...prevValues,
  //       pickupContact: {
  //         fullName: prevValues.fullName,
  //         phone: prevValues.phone
  //       }
  //     }))
  //   } else {
  //     setFormValues((prevValues) => ({
  //       ...prevValues,
  //       pickupContact: {
  //         fullName: '',
  //         phone: ''
  //       }
  //     }))
  //   }
  // }
  const dateTimeSection = (
    <Section label='Date & Time'>
      <AdasBox>
        <SchedulerDatePicker
          fullWidth
          label='Date'
          variant='outlined'
          name='appointmentDate'
          InputLabelProps={{ shrink: true }}
          value={formValues.appointmentDate}
          renderInput={(params) => <AdasTextField {...params} />}
          format={DATE_FORMATS.ISO_DATE_STRING}
          onChange={handleChange}
          className='w-full mb-4'
          minDate={dayjs()}
          maxDate={maxDateFromOrganisation}
        />
        <AdasFormControl fullWidth className='mb-4'>
          <AdasInputLabel id='appointment-time-label'>{isEditForm ? 'Start Time' : 'Time'}</AdasInputLabel>
          <AdasSelect
            labelId='appointment-time-label'
            name='appointmentTime'
            value={formValues.appointmentTime}
            onChange={timeSlotChangeHandler}
            label={isEditForm ? 'Start Time' : 'Time'}
            id='selectItems'
            options={startOptions}
            disabled={timeSlots === null || ''}
          />
        </AdasFormControl>
        {isEditForm && (
          <AdasFormControl fullWidth className='mb-4'>
            <AdasInputLabel id='appointment-end-time-label'>End Time</AdasInputLabel>
            <AdasSelect
              labelId='appointment-end-time-label'
              name='appointmentEndTime'
              value={formValues.appointmentEndTime}
              onChange={timeSlotChangeHandler}
              label='End Time'
              id='selectEndItems'
              options={endOptions}
              disabled={timeSlots === null || ''}
            />
          </AdasFormControl>
        )}
      </AdasBox>
    </Section>
  )

  const pickDeliverField = (
    <AdasBox className='mb-4'>
      <AdasFormControlLabel
        control={<AdasCheckbox checked={formValues?.pickDeliver} onChange={handleChange} name='pickDeliver' />}
        label='Include Vehicle Pickup & Delivery'
        color='#21212199'
        variant='body2'
      />
    </AdasBox>
  )

  // Booking from a quote: the client and vehicle are carried over from the quote and shown read-only
  // (changes to them belong on the quote). Date, time, pickup and the attached files stay editable.
  if (quoteLocked) {
    const vehicle = [formValues.vehicleYear, formValues.vehicleMake, formValues.vehicleModel].filter(Boolean).join(' ')
    const fileDescription = quoteDocumentsLoading ? 'Loading from the quote…' : 'Optional — PDF, DOCX, JPG, or PNG (max 10 MB)'
    return (
      <>
        {dateTimeSection}
        <Section label='Contact Info'>
          <SummaryRow label='Full Name' value={formValues.fullName} />
          <SummaryRow label='Business Name' value={formValues.businessName} />
          <SummaryRow label='Business Phone' value={formValues.businessPhone} />
          <SummaryRow label='Primary Email' value={formValues.primaryEmail} />
        </Section>
        <Section label='Vehicle Info'>
          <AdasBox className='mb-4'>
            <SummaryRow label='Vehicle' value={vehicle} />
            <SummaryRow label='VIN' value={formValues.vin} />
            <SummaryRow label='RO #' value={formValues.aro} />
          </AdasBox>
          {pickDeliverField}
          <AdasBox className='mb-4'>
            <AppointmentAttachment
              name='recentEstimate'
              value={formValues.recentEstimate}
              onChange={handleChange}
              onDelete={() => handleFileDelete('recentEstimate')}
              label='Most Recent Estimate'
              description={fileDescription}
              disabled={quoteDocumentsLoading}
            />
          </AdasBox>
          <AdasBox className='mb-4'>
            <AppointmentAttachment
              name='calibrationFile'
              value={quoteCalibrationReport}
              onChange={handleChange}
              onDelete={() => handleFileDelete('calibrationFile')}
              label='Calibration Report'
              description={fileDescription}
              disabled={quoteDocumentsLoading}
            />
          </AdasBox>
        </Section>
      </>
    )
  }

  return (
    <>
      {dateTimeSection}
      <Section label='Contact Info'>
        <>
          <AdasBox className='mb-4'>
            <AdasTextField
              fullWidth
              name='fullName'
              label='Full Name'
              value={formValues?.fullName}
              onChange={handleChange}
              error={!formValid.fullName}
              helperText={!formValid.fullName ? TEXT_CONSTANTS.INVALID_NAME : ''}
              inputProps={{ maxLength: 50 }}
            />
          </AdasBox>
          <AdasBox className='mb-4'>
            <AdasFormControlLabel
              control={
                <AdasCheckbox checked={formValues?.useExistingClient} onChange={useExistingClientChangeHandle} name='useExistingClient' />
              }
              label='Select business from client list'
            />
          </AdasBox>
          {formValues.useExistingClient && (
            <AdasBox className='mb-4'>
              <AdasFormControl fullWidth>
                <AdasInputLabel id='business-id-label'>Business Name</AdasInputLabel>
                <AdasSelect
                  label='Business Name'
                  labelId='business-id-label'
                  id='business-name-label'
                  name='businessId'
                  value={formValues.businessId}
                  onChange={existingBusinessNameChangeHandle}
                  options={clients || []}
                />
              </AdasFormControl>
            </AdasBox>
          )}
          {!formValues.useExistingClient && (
            <AdasBox className='mb-4'>
              <AdasTextField
                fullWidth
                name='businessName'
                label='Business Name'
                value={formValues.businessName}
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </AdasBox>
          )}
          <AdasBox className='mb-4'>
            <AdasPhoneField
              fullWidth
              name='businessPhone'
              label='Business Phone'
              value={formValues.businessPhone}
              onChange={handleChange}
              disabled={formValues.useExistingClient}
              isValid={formValid.businessPhone}
            />
          </AdasBox>
          <AdasBox className='mb-4'>
            <AdasPhoneField
              fullWidth
              name='phone'
              label='Mobile Phone (optional)'
              value={formValues.phone}
              onChange={handleChange}
              isValid={formValid.phone}
            />
          </AdasBox>

          <AdasBox className='mb-4'>
            <AdasTextField
              fullWidth
              name='primaryEmail'
              label='Primary Email'
              value={formValues.primaryEmail}
              onChange={handleChange}
              disabled={formValues.useExistingClient}
              error={!formValid.primaryEmail}
              helperText={!formValid.primaryEmail ? TEXT_CONSTANTS.INVALID_EMAIL : ''}
              inputProps={{ maxLength: 60 }}
            />
          </AdasBox>
          <AdasBox className='mb-4'>
            <AdasTextField
              fullWidth
              name='secondaryEmail'
              label='Secondary Email (optional)'
              value={formValues.secondaryEmail}
              onChange={handleChange}
              error={!formValid.secondaryEmail}
              helperText={!formValid.secondaryEmail ? TEXT_CONSTANTS.INVALID_EMAIL : ''}
              inputProps={{ maxLength: 60 }}
            />
          </AdasBox>
          <AdasBox className='mb-4'>
            <AdasFormControl fullWidth>
              {referralSourceList.length > 0 ? (
                <>
                  <AdasInputLabel id='referral_id_label'>Referral (optional)</AdasInputLabel>
                  <AdasSelect
                    labelId='referral_id_label'
                    id='referral_id'
                    name='referral'
                    label='Referral (optional)'
                    value={formValues.referral}
                    onChange={handleChange}
                    options={referralSourceList}
                    disabled={formEditValues?.referral}
                    className='w-full text-left'
                  />
                </>
              ) : (
                <>
                  <AdasAlert severity='warning' className='rounded border border-[#FFE58F] text-[#614700] bg-[#FFFBE6]'>
                    {' '}
                    Please contact support to enroll in National Agreements
                  </AdasAlert>
                </>
              )}
            </AdasFormControl>
          </AdasBox>
          {/* <AdasTypography variant='body1' gutterBottom>
                Pickup/deliver the vehicle{' '}
              </AdasTypography>
              <AdasBox className="mb-4">
                <AdasFormControl>
                  <AdasRadioGroup
                    row
                    options={[
                      { label: 'Yes', value: 'true' },
                      { label: 'No', value: 'false' }
                    ]}
                    aria-labelledby='type-group'
                    name='pickDeliver'
                    value={formValues.pickDeliver}
                    onChange={handleChange}
                  />
                </AdasFormControl>
              </AdasBox>
              <AdasTypography variant='body1' gutterBottom mb={2}>
                Address details are optional
              </AdasTypography>
              <AdasBox className="mb-4">
                <AdasTextField
                  fullWidth
                  name='address'
                  label='Address'
                  value={formValues.address}
                  onChange={handleChange}
                  error={!formValid.address}
                  helperText={!formValid.address ? TEXT_CONSTANTS.INAVLID_ALPHANUMERIC : ''}
                  inputProps={{ maxLength: 50 }}
                />
              </AdasBox>
              <AdasBox className="mb-4">
                <AdasPhoneField
                  fullWidth
                  name='phone'
                  label='Phone'
                  value={formValues.phone}
                  onChange={handleChange}
                  isValid={formValid.phone}
                />
              </AdasBox>
              <AdasBox className="mb-4">
                <AdasTextField
                  fullWidth
                  name='city'
                  label='City'
                  value={formValues.city}
                  onChange={handleChange}
                  error={!formValid.city}
                  helperText={!formValid.city ? TEXT_CONSTANTS.INAVLID_ALPHANUMERIC : ''}
                  inputProps={{ maxLength: 50 }}
                />
              </AdasBox>
              <AdasSearchMenu
                fullWidth
                id='select-state'
                select
                label='State *'
                variant='outlined'
                onChange={handleChange}
                className="mb-4"
                name='state'
                value={formValues.state}
                options={usStates.states.map((state) => ({
                  value: state.name, // Use state.name instead of state.abbreviation
                  name: state.name
                }))}
              />
              <AdasBox className="mb-4">
                <AdasTextField
                  fullWidth
                  name='zipCode'
                  type='text'
                  label='ZIP Code'
                  value={formValues.zipCode}
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/\D/g, '')
                    handleChange({
                      target: {
                        name: 'zipCode',
                        value: newValue.slice(0, 5)
                      }
                    })
                  }}
                  error={!formValid.zipCode}
                  helperText={!formValid.zipCode ? TEXT_CONSTANTS.INVALID_ZIP : ''}
                  inputProps={{ maxLength: 5 }}
                />
              </AdasBox> */}
          {/* <AdasTypography variant='body1' gutterBottom mb={2}>
                Pickup details are optional
              </AdasTypography>
              <AdasBox className="mb-4">
                <AdasFormGroup>
                  <AdasFormControlLabel
                    control={<AdasCheckbox checked={sameAddress} onChange={handleSameAddressChange} name='sameAddress' />}
                    label='Same as address'
                  />
                </AdasFormGroup>
              </AdasBox>
              <AdasBox className="mb-4">
                <AdasTextField
                  fullWidth
                  name='fullName'
                  label='Pickup Name'
                  value={formValues.pickupContact.fullName}
                  onChange={handlePickupContactChange}
                  disabled={sameAddress}
                  error={!formValid.pickupContact.fullName}
                  helperText={!formValid.pickupContact.fullName ? TEXT_CONSTANTS.INVALID_NAME : ''}
                  inputProps={{ maxLength: 50 }}
                />
              </AdasBox>

              <AdasBox className="mb-4">
                <AdasPhoneField
                  fullWidth
                  name='phone'
                  label='Pickup Phone Number'
                  value={formValues.pickupContact.phone}
                  onChange={handlePickupContactChange}
                  disabled={sameAddress}
                  isValid={formValid.pickupContact.phone}
                />
              </AdasBox> */}
        </>
      </Section>
      <Section label='Vehicle Info'>
        <>
          <AdasBox className='mb-4'>
            <AdasFormControlLabel
              control={
                <AdasCheckbox
                  checked={formValues.useVin}
                  onChange={vinChangeHandle}
                  name='useVin'
                  aria-describedby={formValues.useVin ? 'use-vin-hint' : undefined}
                />
              }
              label='Use VIN'
            />
            {/* CAR-4065: with Use VIN ticked, Year/Make/Model are locked text boxes - say how to get the lists. */}
            {formValues.useVin && (
              <p id='use-vin-hint' className='text-[12px] text-[#6B7280]'>
                No VIN? Untick to pick Year, Make and Model.
              </p>
            )}
          </AdasBox>
          {formValues.useVin && (
            <AdasBox className='mb-4'>
              <VinInput updateVehicleInfo={updateVehicleInfo} prepopulateVin={formValues.vin} isScan={false} />
            </AdasBox>
          )}

          <AdasBox className='mb-4'>
            {formValues.useVin && (
              <AdasTextField
                fullWidth
                name='vehicleYear'
                label='Year'
                value={formValues.vehicleYear}
                onChange={handleChange}
                disabled={formValues.useVin}
              />
            )}
            {!formValues.useVin && (
              <AdasFormControl fullWidth className='mb-4'>
                <AdasInputLabel id='vehicle-year-label'>Year</AdasInputLabel>
                <AdasSelect
                  fullWidth
                  id='vehicleYear'
                  labelId='vehicle-year-label'
                  name='vehicleYear'
                  label='Year'
                  value={formValues?.vehicleYear}
                  onChange={handleVehicleFormChange}
                  options={years}
                />
              </AdasFormControl>
            )}
          </AdasBox>
          <AdasBox className='mb-4'>
            {formValues.useVin && (
              <AdasTextField
                fullWidth
                name='vehicleMake'
                label='Make'
                value={formValues.vehicleMake}
                onChange={handleChange}
                disabled={formValues.useVin}
              />
            )}
            {!formValues.useVin && (
              <AdasFormControl fullWidth className='mb-4'>
                <AdasInputLabel id='vehicle-Make-label'>Make</AdasInputLabel>
                <AdasSelect
                  fullWidth
                  id='vehicleMake'
                  labelId='vehicle-Make-label'
                  name='vehicleMake'
                  label='Make'
                  value={formValues?.vehicleMake}
                  onChange={handleVehicleFormChange}
                  options={makes}
                  placeholder={formValues?.vehicleYear ? 'Select...' : 'Select a year first'}
                  className='data-[placeholder]:text-[#6B7280]'
                />
              </AdasFormControl>
            )}
          </AdasBox>
          <AdasBox className='mb-4'>
            {formValues.useVin && (
              <AdasTextField
                fullWidth
                name='vehicleModel'
                label='Model'
                value={formValues.vehicleModel}
                onChange={handleChange}
                disabled={formValues.useVin}
              />
            )}
            {!formValues.useVin && (
              <AdasFormControl fullWidth className='mb-4'>
                <AdasInputLabel id='vehicle-Model-label'>Model</AdasInputLabel>
                <AdasSelect
                  fullWidth
                  id='vehicleModel'
                  labelId='vehicle-Model-label'
                  name='vehicleModel'
                  label='Model'
                  value={formValues?.vehicleModel}
                  onChange={handleVehicleFormChange}
                  options={models}
                  placeholder={formValues?.vehicleMake ? 'Select...' : 'Select a make first'}
                  className='data-[placeholder]:text-[#6B7280]'
                />
              </AdasFormControl>
            )}
          </AdasBox>
          <AdasBox className='mb-4'>
            <AdasTextField
              fullWidth
              name='aro'
              label='RO # (optional)'
              value={formValues.aro}
              onChange={handleChange}
              error={!formValid.aro}
              helperText={!formValid.aro ? TEXT_CONSTANTS.INAVLID_ALPHANUMERIC : ''}
              inputProps={{ maxLength: 15 }}
            />
          </AdasBox>
          {pickDeliverField}
          <AdasBox className='mb-4'>
            <AdasTextField
              fullWidth
              name='notes'
              label='Additional Notes'
              value={formValues.notes}
              onChange={handleChange}
              inputProps={{ maxLength: MAX_NOTES_LENGTH }}
              helperText={`Maximum ${MAX_NOTES_LENGTH} characters (${MAX_NOTES_LENGTH - (formValues.notes?.length || 0)} remaining).`}
            />
          </AdasBox>
          <AdasBox className='mb-4'>
            <AppointmentAttachment
              name='recentEstimate'
              value={formValues.recentEstimate}
              onChange={handleChange}
              onDelete={handleFileDelete}
              label='Most Recent Estimate'
              description='Optional — PDF, DOCX, JPG, or PNG (max 10 MB)'
            />
          </AdasBox>
        </>
      </Section>
    </>
  )
}
